import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { catchError, debounceTime, finalize } from 'rxjs/operators';
import { CustomFieldDefinition } from 'src/app/custom-fields/custom-fields.model';
import { CustomFieldsService } from 'src/app/custom-fields/custom-fields.service';
import { WhatsAppTemplateStatusEnum } from 'src/app/parameters/message-templates/whatsapp-templates/shared';
import { WhatsappTemplateButtonTypeEnum } from 'src/app/parameters/message-templates/whatsapp-templates/shared/models/whatsapp-template-button-type.enum';
import { WhatsappTemplatesService } from 'src/app/parameters/message-templates/whatsapp-templates/whatsapp-templates.service';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { EditorConfig } from 'src/app/shared/editor-config';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { AttachmentMediaTypeEnum } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';

import DateUtils from 'src/app/shared/utils/date.utils';
import { SelectWhatsappTemplateComponent } from '../select-whatsapp-template/select-whatsapp-template.component';
import { Template } from '../select-whatsapp-template/shared/template.model';
import { WhatsappTemplate } from '../select-whatsapp-template/shared/whatsapp-template.model';
import { LimitedTimeOfferExpirationTypeEnum } from './limited-time-offer-expiration-type.enum';

@Component({
  selector: 'app-chat-bot-whatsapp-template-editor',
  templateUrl: './chat-bot-whatsapp-template-editor.component.html',
  styleUrls: ['./chat-bot-whatsapp-template-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChatBotWhatsappTemplateEditorComponent implements OnInit, OnDestroy {
  @Input() availableTemplates: Template[] = [];
  @Input() selected: WhatsappTemplate = {} as WhatsappTemplate;
  @Input() siteId: number;
  @Input() scenarioId: number;
  @Input() boxId: number;
  @Input() lang: string;
  @Output() checkValidity: EventEmitter<WhatsappTemplate> = new EventEmitter();

  templatePreview: string;

  headerLabel: string = this.basicService.translate('task/whatsapp-template/section-header');
  titleLabel: string = this.basicService.translate('task/whatsapp-template/tooltip');
  selectTemplateLabel = '';
  uploading: boolean;
  uploadProgress$: BehaviorSubject<number> = new BehaviorSubject(0);
  AttachmentMediaTypeEnum = AttachmentMediaTypeEnum;
  LimitedTimeOfferExpirationTypeEnum = LimitedTimeOfferExpirationTypeEnum;
  supportedMediaTypes: string;
  uploadMediaTypeLabel: string;
  editMediaTypeLabel: string;
  downloadMediaTypeLabel: string;
  expirationDelayHumanized: string;

  expirationTypeControl = new FormControl(LimitedTimeOfferExpirationTypeEnum.DATE_TIME_PICKER);

  @ViewChild('fileInput') upload: ElementRef<HTMLInputElement>;
  @ViewChild('selectExpirationDelayRef') selectExpirationDelayRef: TemplateRef<any>;

  private templateVariablesPattern = /\{\{[0-9]+\}\}/g;
  customFieldsMap: Map<number, any> = new Map();
  customFieldsChoices: Choice[] = [
    { id: 1, label: 'yes' },
    { id: 0, label: 'no' },
  ];

  form = this.formBuilder.group({
    params: this.formBuilder.array([]),
    urlButtonParams: this.formBuilder.array([]),
    couponCodeButtonParams: this.formBuilder.array([]),
    limitedTimeOfferExpirationDate: [null],
  });

  months: Choice[] = Array.from({ length: 12 }).map((_, id) => ({ id, label: `${id}` }));
  days: Choice[] = Array.from({ length: 31 }).map((_, id) => ({ id, label: `${id}` }));
  hours: Choice[] = Array.from({ length: 24 }).map((_, id) => ({ id, label: `${id}` }));
  minutes: Choice[] = Array.from({ length: 60 }).map((_, id) => ({ id, label: `${id}` }));

  expirationDelayForm = this.formBuilder.group({
    months: [0],
    days: [0],
    hours: [0],
    minutes: [0],
  });
  paramsSubscriptions: Subscription;
  urlButtonParamsSubscriptions: Subscription;

  displayDateTimeModal: boolean;

  get params() {
    return this.form.controls['params'] as FormArray;
  }

  get couponCodeButtonParams() {
    return this.form.controls['couponCodeButtonParams'] as FormArray;
  }

  get urlButtonParams() {
    return this.form.controls['urlButtonParams'] as FormArray;
  }

  private subscriptions: Subscription = new Subscription();

  constructor(
    private basicService: BasicService,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private errorHandlingService: ErrorHandlingService,
    private whatsappTemplatesService: WhatsappTemplatesService,
    private customFieldsService: CustomFieldsService
  ) {}

  ngOnInit() {
    this.customFieldsService
      .getFields()
      .pipe(catchError(() => of([])))
      .subscribe({
        next: (customFields: CustomFieldDefinition[]) => {
          this.customFieldsChoices = customFields
            .filter((customField: CustomFieldDefinition) => customField.scope === 'user')
            .map(({ id, label, type }) => {
              this.customFieldsMap.set(id, label);
              const labelValue = type?.type ? `${label} (${type.type})` : label;
              return { id, label: labelValue };
            });
          this.initTemplate();
        },
      });
  }

  private initTemplate(): void {
    if (this.selected.template) {
      this.selected.finalText = this.templatePreview;
      if (Array.isArray(this.selected.params)) {
        this.selected.params = this.selected.params.reduce(
          (dictionary: { [key: string]: { value: string; type: string } }, value, index) => {
            dictionary[`{{${index + 1}}}`] = { value, type: 'text' };
            return dictionary;
          },
          {}
        );
      }
      this.initFormFromParams();
      this.enrichFormFromButtons();
      this.getSupportedMediaTypes();
      this.updatePreviewAndValidity();
    } else {
      this.selected.template = null;
      this.selected.params = {};
      this.selected.finalText = null;
      this.templatePreview = '';
    }
    this.translateLabel();

    this.listenLimitedTimeOffer();
  }

  private resetForm() {
    this.form = this.formBuilder.group({
      params: this.formBuilder.array([]),
      urlButtonParams: this.formBuilder.array([]),
      couponCodeButtonParams: this.formBuilder.array([]),
    });
  }

  private initFormFromParams() {
    if (!this.selected.template.displayed_placeholders) {
      return;
    }

    this.resetForm();
    this.paramsSubscriptions?.unsubscribe();
    this.paramsSubscriptions = new Subscription();
    (Object.keys(this.selected.template.displayed_placeholders) || []).forEach((_, index) => {
      const paramForm = this.formBuilder.group({
        text: [''],
        customField: [''],
      });

      try {
        const { value, type } = this.selected.params[`{{${index + 1}}}`];
        let defaultValue = {};
        if (type === 'custom-field') {
          defaultValue = { customField: value, text: this.selected.params[`{{${index + 1}}}`].default };
          paramForm.get('text').addValidators(Validators.required);
        } else {
          defaultValue = { text: value };
        }
        paramForm.patchValue(defaultValue);
        this.paramsSubscriptions.add(
          paramForm.get('customField').valueChanges.subscribe(value => {
            if (value) {
              paramForm.get('text').addValidators(Validators.required);
            } else {
              paramForm.get('text').clearValidators();
            }
          })
        );
        this.updatePreviewAndValidity();
      } catch {
        //
      }
      this.paramsSubscriptions.add(
        paramForm.valueChanges.pipe(debounceTime(100)).subscribe(() => this.updatePreviewAndValidity())
      );
      this.params.push(paramForm);
    });
  }

  private enrichFormFromButtons() {
    if (!this.selected.template.buttons) {
      return;
    }
    this.urlButtonParamsSubscriptions?.unsubscribe();
    this.urlButtonParamsSubscriptions = new Subscription();
    const copyCodeButton = this.selected.template.buttons.filter(
      ({ type, example }) => type === WhatsappTemplateButtonTypeEnum.COPY_CODE && example
    );
    if (copyCodeButton?.length) {
      copyCodeButton.forEach(({ example }) => {
        const buttonForm = this.formBuilder.group({
          example: [this.selected.couponCodeButtonParams?.couponCode || example, Validators.required],
        });

        this.urlButtonParamsSubscriptions.add(
          buttonForm.valueChanges.pipe(debounceTime(100)).subscribe(() => this.updatePreviewAndValidity())
        );
        this.couponCodeButtonParams.push(buttonForm);
      });
    } else {
      delete this.selected.couponCodeButtonParams;
    }
    const suffixedButton = this.selected.template.buttons.filter(
      ({ type, example }) => type === WhatsappTemplateButtonTypeEnum.URL && example
    );
    if (suffixedButton?.length) {
      suffixedButton.forEach(button => {
        const { url, example, text } = button;
        const exampleValue = Array.isArray(example) ? example[0] : example;
        const buttonForm = this.formBuilder.group({
          text: [text],
          url: [url.replace('{{1}}', '')],
          example: [exampleValue, Validators.required],
        });

        this.urlButtonParamsSubscriptions.add(
          buttonForm.valueChanges.pipe(debounceTime(100)).subscribe(() => this.updatePreviewAndValidity())
        );
        this.urlButtonParams.push(buttonForm);
        // trick to change the label gap on the fly avec the synchronous life cycle.
        setTimeout(() => {
          const elements = document.querySelectorAll('.url-buttons-param');
          (elements || []).forEach(element => {
            element.querySelector('.mat-form-field-label-wrapper').style.overflowX = 'visible';
            element.querySelector('.mat-form-field-label-wrapper').style.overflowY = 'visible';
            element.querySelector('.mat-form-field-label').style.transform = `translateX(-${
              element.querySelector('.mat-form-field-prefix').offsetWidth
            }px) translateY(-0.59375em) scale(0.75)`;
          });
        });
      });
    }
  }

  /**
   * Depends on the header media type defined in the WA BSM, the supported types are either image or pdf.
   */
  private getSupportedMediaTypes() {
    if (!this.selected.template.header) {
      return;
    }
    switch (this.selected.template.header.media_type) {
      case AttachmentMediaTypeEnum.Image:
        this.supportedMediaTypes = 'image/jpeg,image/png';
        this.uploadMediaTypeLabel = this.basicService.translate(
          'parameters/quick_replies/details/graph/whatsapp_message_with_buttons/edit_box_header_add_image'
        );
        this.editMediaTypeLabel = this.basicService.translate(
          'parameters/quick_replies/details/graph/whatsapp_message_with_buttons/edit_box_header_change_image'
        );
        break;
      case AttachmentMediaTypeEnum.Document:
        this.supportedMediaTypes = 'application/pdf';
        this.uploadMediaTypeLabel = this.basicService.translate(
          'parameters/quick_replies/details/graph/whatsapp_message_with_buttons/edit_box_header_add_file'
        );
        this.downloadMediaTypeLabel = this.basicService.translate(
          'parameters/quick_replies/details/graph/whatsapp_message_with_buttons/edit_box_header_download_file'
        );
        this.editMediaTypeLabel = this.basicService.translate(
          'parameters/quick_replies/details/graph/whatsapp_message_with_buttons/edit_box_header_change_file'
        );
        break;
      case AttachmentMediaTypeEnum.Video:
        this.supportedMediaTypes = 'video/mp4,video/3gpp';
        this.uploadMediaTypeLabel = this.basicService.translate(
          'parameters/quick_replies/details/graph/whatsapp_message_with_buttons/edit_box_header_add_video'
        );
        this.editMediaTypeLabel = this.basicService.translate(
          'parameters/quick_replies/details/graph/whatsapp_message_with_buttons/edit_box_header_change_video'
        );
        break;
      default:
        break;
    }
  }

  updatePreviewAndValidity() {
    this.templatePreview = this.selected.template.text.replace(this.templateVariablesPattern, this.variablesReplacer);
    this.selected.finalText = this.templatePreview;
    if (this.urlButtonParams?.value.length) {
      if (!this.selected.urlButtonParams) {
        this.selected.urlButtonParams = {};
      }

      this.selected.urlButtonParams = this.urlButtonParams.value.reduce((result, { text, example }) => {
        result[text] = example;
        return result;
      }, {});
    }
    if (this.couponCodeButtonParams?.value.length) {
      if (!this.selected.couponCodeButtonParams) {
        this.selected.couponCodeButtonParams = { couponCode: '' };
      }

      this.selected.couponCodeButtonParams = this.couponCodeButtonParams.value.reduce(
        (result, { example }) => {
          result.couponCode = example;
          return result;
        },
        { couponCode: '' }
      );
    }
    // Let the angularjs parent controller check the validity and update the buttons to save.
    this.checkValidity.emit(this.selected);
  }

  private variablesReplacer = match => {
    const index = parseInt(match.match(/[0-9]+/g)[0]);
    const { text, customField } = this.params.at(index - 1).value; // whatsapp templates variables indexes start at 1: {{1}}.
    const textValue = text === 0 ? '0' : text;
    const customFieldLabel = this.customFieldsMap.get(customField);
    const customFieldValue = customFieldLabel === 0 ? '0' : customFieldLabel;
    if (Array.isArray(this.selected.params)) {
      this.selected.params = {};
    }

    if (!this.selected.params[`{{${index}}}`]) {
      this.selected.params[`{{${index}}}`] = { value: '', type: 'text' };
    }
    const type = customFieldValue ? 'custom-field' : 'text';

    this.selected.params[`{{${index}}}`] = {
      value: customField || textValue || '',
      type,
    };
    if (type === 'custom-field') {
      this.selected.params[`{{${index}}}`].default = text;
    }
    const customFieldValueFormatted = customFieldValue ? `[${customFieldValue}]` : null;
    return (
      customFieldValueFormatted || textValue || this.selected.template.displayed_placeholders[match].label || match
    );
  };

  private translateLabel() {
    this.selectTemplateLabel = this.basicService.translate(
      this.selected.template ? 'task/whatsapp-template/select-other-template' : 'task/whatsapp-template/select-template'
    );
  }

  trackByIndex(index: number): number {
    return index;
  }

  uploadMedia($event) {
    const file = $event.target.files?.item(0);
    const { header } = this.selected.template;

    if (header) {
      const { media_type } = header;
      const editor = new EditorConfig(SiteTypeEnum.WhatsApp, { isPrivate: true });
      // Specifically for this use case, overrides because only pdf files are accepted.
      editor.fileMimeTypes = 'application/pdf';
      try {
        editor.checkLimitations(media_type, file);
      } catch ({ translationPath, translationData }) {
        this.basicService.presentAlert({
          subHeader: `${this.basicService.translate(translationPath, translationData)}`,
          buttons: [this.basicService.translate('m/ok')],
        });
        return;
      }

      // The picture is valid for WhatsApp in private then we can try to upload this one.
      this.uploading = true;
      this.uploadProgress$.next(0);

      try {
        this.selected.template.header.filename = file.name || this.basicService.translate('attachments/File');

        this.whatsappTemplatesService
          .uploadMediaForBot(this.selected.template.id, this.siteId, this.scenarioId, this.boxId, file)
          .pipe(
            this.errorHandlingService.catchAlertAndThrowHttpError(),
            finalize(() => {
              this.checkValidity.emit(this.selected);
              this.uploading = false;
              this.upload = null;
              // SP-5133
              // this.headerCustomFieldCtrl.reset();
            })
          )
          .subscribe({
            next: ({ headerMediaUrl }) => {
              this.selected.template.header.media_url = headerMediaUrl;
            },
          });
      } catch ({ translationPath, translationData }) {
        this.upload = null;
        this.uploading = false;
        this.removeMedia();

        this.basicService.presentAlert({
          subHeader: `${this.basicService.translate(translationPath, translationData)}`,
          buttons: [this.basicService.translate('m/ok')],
        });
      }
    }
  }

  private removeMedia() {
    const { template } = this.selected;
    const { header } = template;
    this.selected.template = {
      ...template,
      header: {
        ...header,
        media_url: null,
        filename: null,
      },
    };
    this.checkValidity.emit(this.selected);
  }

  openSelectWhatsappTemplate(): void {
    const availableTemplates = this.availableTemplates.filter(
      (template: Template) => !template.status || template.status === WhatsAppTemplateStatusEnum.approved
    );
    const dialogRef = this.dialog.open(SelectWhatsappTemplateComponent, {
      panelClass: ['large-size'],
      data: { initialLang: this.lang, availableTemplates },
    });

    dialogRef.afterClosed().subscribe((template: Template) => {
      if (!template) {
        return;
      }
      this.selected.template = template;

      this.selected.params = {};
      this.initFormFromParams();
      this.enrichFormFromButtons();
      this.translateLabel();
      this.listenLimitedTimeOffer();

      this.getSupportedMediaTypes();
      this.updatePreviewAndValidity();
    });
  }

  closeDateTimeModal(dateTime: any) {
    this.displayDateTimeModal = false;

    if (!dateTime) {
      return;
    }

    this.selected.limitedTimeOffer = undefined;
    const { date, time, timezone } = dateTime;
    const expirationDate = DateUtils.createDate({
      year: date.get('year'),
      month: date.get('month'),
      date: date.get('date'),
      hour: time.h,
      minute: time.m,
      second: 0,
      timezone,
    });

    this.selected.limitedTimeOffer = {
      expirationDate: expirationDate.toISOString(),
      expirationDelay: undefined,
    };
    this.checkValidity.emit(this.selected);
  }

  private listenLimitedTimeOffer() {
    if (this.selected.template?.limited_time_offer) {
      console.log('subscribe()');
      this.subscriptions?.unsubscribe();
      this.subscriptions = new Subscription();
      if (!this.selected.limitedTimeOffer) {
        this.selected.limitedTimeOffer = {};
      }
      this.subscriptions.add(
        this.expirationTypeControl.valueChanges.subscribe((expirationType: LimitedTimeOfferExpirationTypeEnum) => {
          if (expirationType === LimitedTimeOfferExpirationTypeEnum.DATE_TIME_PICKER) {
            this.expirationDelayForm.reset();
            this.selected.limitedTimeOffer.expirationDelay = null;
          } else if (expirationType === LimitedTimeOfferExpirationTypeEnum.DELAY) {
            this.selected.limitedTimeOffer.expirationDate = null;
          }
          this.checkValidity.emit(this.selected);
        })
      );
      this.expirationTypeControl.setValue(
        this.selected.limitedTimeOffer.expirationDelay
          ? LimitedTimeOfferExpirationTypeEnum.DELAY
          : LimitedTimeOfferExpirationTypeEnum.DATE_TIME_PICKER
      );

      if (
        this.expirationTypeControl.value === LimitedTimeOfferExpirationTypeEnum.DELAY &&
        this.selected.limitedTimeOffer.expirationDelay
      ) {
        this.expirationDelayForm.patchValue(this.selected.limitedTimeOffer.expirationDelay);
      }

      this.expirationDelayForm.valueChanges.subscribe(value => {
        this.selected.limitedTimeOffer.expirationDelay = value;
        this.selected.limitedTimeOffer.expirationDate = undefined;
        this.checkValidity.emit(this.selected);
      });
    } else {
      this.selected.limitedTimeOffer = undefined;
      this.subscriptions?.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.paramsSubscriptions?.unsubscribe();
    this.urlButtonParamsSubscriptions?.unsubscribe();
  }
}
